/**
 * The reporting window + focus controls for an optimizer run.
 *
 * One component, used by both surfaces that start a run — the sidekick agent's
 * run gate and the production Optimize Campaign modal — so the two cannot come to
 * disagree about what a window means or which platforms can be told one.
 *
 * It renders what the platform can actually honour, and nothing it can't:
 *
 *   'range'  (Google Search / PMax / DemandGen) — presets plus a Custom option
 *            with real start/end inputs, because those handlers take explicit
 *            dates and prefer them over any preset.
 *   'preset' (Reddit / LinkedIn / Facebook) — presets only. An arbitrary range
 *            cannot be expressed to these, so offering one would be a control
 *            that silently rounds the user's answer.
 *   'none'   (DV360 / TTD / MadHive / YouTube DV360) — NOTHING. No picker, no
 *            default, not even a line explaining the absence. These runs send no
 *            window fields at all, so any control here would be describing a
 *            setting that does not exist, and a stated default would be a claim
 *            about behaviour nothing implements.
 *
 * Unstyled beyond two class hooks: each host supplies its own look via
 * `className`, since one lives in a CLW panel and the other in a modal.
 */

import {
  OPTIMIZE_DATE_PRESETS,
  DEFAULT_OPTIMIZE_PRESET,
  optimizeDateRangeSupport,
  latestOptimizeDate,
} from '../utils/optimizeDateRange';

export function OptimizeWindowFields({
  destination,
  // { preset, startDate, endDate, customPrompt }
  value,
  onChange,
  className = '',
  // The focus box is optional on surfaces that already have a composer.
  showPrompt = true,
}) {
  const support = optimizeDateRangeSupport(destination);
  const preset = value?.preset || DEFAULT_OPTIMIZE_PRESET;
  const set = (patch) => onChange?.({ ...value, preset, ...patch });

  return (
    <div className={`opt-window ${className}`}>
      {support === 'none' ? null : (
        <>
          <label className="opt-window-label" htmlFor="opt-window-preset">Analyse</label>
          <select
            id="opt-window-preset"
            className="opt-window-select"
            value={preset}
            onChange={(e) => set({ preset: e.target.value })}
          >
            {OPTIMIZE_DATE_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
            {/* Only where explicit dates are actually honoured. */}
            {support === 'range' && <option value="custom">Custom range…</option>}
          </select>

          {support === 'range' && preset === 'custom' && (
            <div className="opt-window-range">
              <input
                type="date"
                className="opt-window-date"
                aria-label="Start date"
                max={latestOptimizeDate()}
                value={value?.startDate || ''}
                onChange={(e) => set({ startDate: e.target.value })}
              />
              <span className="opt-window-dash">→</span>
              <input
                type="date"
                className="opt-window-date"
                aria-label="End date"
                max={latestOptimizeDate()}
                value={value?.endDate || ''}
                onChange={(e) => set({ endDate: e.target.value })}
              />
            </div>
          )}
        </>
      )}

      {showPrompt && (
        <textarea
          className="opt-window-prompt"
          rows={2}
          maxLength={500}
          value={value?.customPrompt || ''}
          onChange={(e) => set({ customPrompt: e.target.value })}
          placeholder={'Optional — what should this run focus on? e.g. "cut CPA on the branded ad groups, don\'t touch budgets"'}
        />
      )}
    </div>
  );
}

export default OptimizeWindowFields;
